/** Filter bar for the Waste Management Dashboard demo screen. Site/region/category/reason/
 * avoidability/unit basis are wired to actually filter the breakdown, reasons and site tables
 * below (see index.tsx); the remaining filters (date range, site group, client, supplier,
 * product, menu item, waste stage, disposal method, performance band) are illustrative
 * selectors over the same single-period mock dataset - there is no multi-period/multi-tenant
 * backend feed to filter against yet (see header comment in `data.ts`). */

import { Grid, MenuItem, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  CATEGORIES,
  MENU_ITEMS,
  PRODUCT_WASTE,
  REGIONS,
  SITE_GROUPS,
  SITES,
  SUPPLIERS,
  WASTE_REASONS,
} from '../data';
import { WasteFilters } from '../types';

interface FiltersBarProps {
  filters: WasteFilters;
  onChange: (filters: WasteFilters) => void;
}

export const ALL_FILTER_VALUE = 'All';

const DATE_RANGES = ['This week', 'Last 4 weeks', 'Last 13 weeks', 'Year to date'];
const WASTE_STAGES = ['Purchased', 'Received', 'Stored', 'Prepared', 'Produced', 'Sold', 'Wasted', 'Donated', 'Recycled', 'Composted', 'Anaerobic digestion', 'Landfill'];
const DISPOSAL_METHODS = ['Landfill', 'Recycled', 'Composted', 'Anaerobic digestion', 'Donated'];
const AVOIDABILITY = ['Avoidable', 'Unavoidable'];
const UNIT_BASES = ['Value', 'Weight', 'Volume', 'Units'];
const PERFORMANCE_BANDS = ['Top quartile', 'On target', 'Needs attention', 'Critical'];
const CLIENTS = ['Thomas Franks Contract Catering', 'Orderly Retail & Travel', 'Independent Hospitality Group'];

export const initialWasteFilters: WasteFilters = {
  dateRangeLabel: 'Last 4 weeks',
  site: ALL_FILTER_VALUE,
  siteGroup: ALL_FILTER_VALUE,
  region: ALL_FILTER_VALUE,
  client: ALL_FILTER_VALUE,
  supplier: ALL_FILTER_VALUE,
  product: ALL_FILTER_VALUE,
  category: ALL_FILTER_VALUE,
  menuItem: ALL_FILTER_VALUE,
  wasteReason: ALL_FILTER_VALUE,
  wasteStage: ALL_FILTER_VALUE,
  disposalMethod: ALL_FILTER_VALUE,
  avoidability: ALL_FILTER_VALUE,
  unitBasis: ALL_FILTER_VALUE,
  performanceBand: ALL_FILTER_VALUE,
};

const FiltersBar = ({ filters, onChange }: FiltersBarProps) => {
  const { t } = useTranslation('common');
  const set = <K extends keyof WasteFilters>(key: K, value: WasteFilters[K]) => onChange({ ...filters, [key]: value });

  const selectField = (labelKey: string, labelDefault: string, key: keyof WasteFilters, options: string[], allLabelKey: string, allLabelDefault: string) => (
    <Grid item
      xs={12}
      sm={6}
      md={2.4}
      key={key}>
      <TextField label={t(labelKey, labelDefault)}
        select
        size="small"
        fullWidth
        value={filters[key]}
        onChange={(e) => set(key, e.target.value)}>
        <MenuItem value={ALL_FILTER_VALUE}>{t(allLabelKey, allLabelDefault)}</MenuItem>
        {options.map((option) => (
          <MenuItem key={option}
            value={option}>{option}</MenuItem>
        ))}
      </TextField>
    </Grid>
  );

  return (
    <Stack sx={{ mt: 3, mb: 3 }}>
      <Grid container
        spacing={2}>
        <Grid item
          xs={12}
          sm={6}
          md={2.4}>
          <TextField label={t('wasteDashboardDateRange', 'Date range')}
            select
            size="small"
            fullWidth
            value={filters.dateRangeLabel}
            onChange={(e) => set('dateRangeLabel', e.target.value)}>
            {DATE_RANGES.map((range) => (
              <MenuItem key={range}
                value={range}>{range}</MenuItem>
            ))}
          </TextField>
        </Grid>
        {selectField('wasteDashboardSite', 'Site', 'site', SITES, 'wasteDashboardAllSites', 'All sites')}
        {selectField('wasteDashboardSiteGroup', 'Site group', 'siteGroup', SITE_GROUPS, 'wasteDashboardAllSiteGroups', 'All site groups')}
        {selectField('wasteDashboardRegion', 'Region', 'region', REGIONS, 'wasteDashboardAllRegions', 'All regions')}
        {selectField('wasteDashboardClient', 'Client', 'client', CLIENTS, 'wasteDashboardAllClients', 'All clients')}
        {selectField('wasteDashboardSupplier', 'Supplier', 'supplier', SUPPLIERS, 'wasteDashboardAllSuppliers', 'All suppliers')}
        {selectField('wasteDashboardProduct', 'Product', 'product', PRODUCT_WASTE.map((p) => p.product), 'wasteDashboardAllProducts', 'All products')}
        {selectField('wasteDashboardCategory', 'Product category', 'category', CATEGORIES, 'wasteDashboardAllCategories', 'All categories')}
        {selectField('wasteDashboardMenuItem', 'Menu item', 'menuItem', MENU_ITEMS, 'wasteDashboardAllMenuItems', 'All menu items')}
        {selectField('wasteDashboardWasteReason', 'Waste reason', 'wasteReason', [...WASTE_REASONS], 'wasteDashboardAllWasteReasons', 'All waste reasons')}
        {selectField('wasteDashboardWasteStage', 'Waste stage', 'wasteStage', WASTE_STAGES, 'wasteDashboardAllWasteStages', 'All waste stages')}
        {selectField('wasteDashboardDisposalMethod', 'Disposal method', 'disposalMethod', DISPOSAL_METHODS, 'wasteDashboardAllDisposalMethods', 'All disposal methods')}
        {selectField('wasteDashboardAvoidability', 'Avoidable / unavoidable', 'avoidability', AVOIDABILITY, 'wasteDashboardAllAvoidability', 'All waste')}
        {selectField('wasteDashboardUnitBasis', 'Value, weight, volume or units', 'unitBasis', UNIT_BASES, 'wasteDashboardAllUnitBases', 'All measures')}
        {selectField('wasteDashboardPerformanceBand', 'Site performance band', 'performanceBand', PERFORMANCE_BANDS, 'wasteDashboardAllPerformanceBands', 'All performance bands')}
      </Grid>
    </Stack>
  );
};

export default FiltersBar;
